import React from "react";
import { Mail, MapPin, Clock, MessageCircle, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { business, fullAddress } from "@/lib/business";
import { useSeo } from "@/lib/seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = React.useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = React.useState(false);
  const [sent, setSent] = React.useState(false);
  useSeo({
    title: "Contact NovalynxTech — Support, warranty claims & order help",
    description: `Reach the NovalynxTech support team for order questions, warranty claims and returns. Email ${business.supportEmail} — replies within 24 hours (usually faster).`,
    canonical: typeof window !== "undefined" ? window.location.origin + "/contact" : "",
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.trim() || !form.message.trim()) {
      toast.error("Email and message are required"); return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success("Message sent — we'll reply within 24 hours");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong");
    }
    setSending(false);
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="contact-page">
      <p className="text-xs uppercase tracking-widest text-primary font-semibold mb-3">Get in touch</p>
      <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mb-3">Talk to a human</h1>
      <p className="text-lg text-muted-foreground mb-12 max-w-2xl">
        Order question, warranty claim, or just a hello — drop us a message and we'll get back within 24 hours (usually much faster).
      </p>

      <div className="grid gap-10 lg:grid-cols-[1fr_360px]">
        {/* Form */}
        {sent ? (
          <div className="rounded-2xl bg-primary/10 border border-primary/40 p-8" data-testid="contact-sent">
            <MessageCircle className="h-10 w-10 text-primary mb-4" />
            <h2 className="font-display text-2xl font-bold mb-2">Message received</h2>
            <p className="text-muted-foreground">
              Thanks {form.name || "for reaching out"} — we've saved your message and a real person will reply to <span className="text-primary">{form.email}</span> within 24 hours.
            </p>
            <Button variant="outline" className="border-white/15 mt-6" onClick={() => { setSent(false); setForm({ name: "", email: "", subject: "", message: "" }); }} data-testid="contact-send-another">
              Send another
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="rounded-2xl bg-card border border-white/5 p-6 sm:p-8 space-y-4" data-testid="contact-form">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label>Your name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name" />
              </div>
              <div>
                <Label>Email <span className="text-primary">*</span></Label>
                <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email" />
              </div>
            </div>
            <div>
              <Label>Subject</Label>
              <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Order #NX…, warranty, general question…" data-testid="contact-subject" />
            </div>
            <div>
              <Label>Message <span className="text-primary">*</span></Label>
              <Textarea rows={7} required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message" />
            </div>
            <Button type="submit" className="btn-primary w-full h-12" disabled={sending} data-testid="contact-submit">
              {sending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Sending…</> : "Send message"}
            </Button>
          </form>
        )}

        {/* Side info */}
        <div className="space-y-4">
          <div className="rounded-2xl bg-card border border-white/5 p-6">
            <div className="flex items-start gap-3">
              <Mail className="h-5 w-5 text-primary shrink-0 mt-0.5" />
              <div>
                <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Email</p>
                <a href={`mailto:${business.supportEmail}`} className="text-white hover:text-primary transition" data-testid="contact-email-link">{business.supportEmail}</a>
              </div>
            </div>
          </div>

          <div className="rounded-2xl bg-card border border-white/5 p-6">
            <div className="flex items-start gap-3">
              <Clock className="h-5 w-5 text-primary shrink-0 mt-0.5" />
              <div>
                <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Response time</p>
                <p className="text-sm">Usually within 24 hours · Mon–Fri</p>
              </div>
            </div>
          </div>

          <div className="rounded-2xl bg-card border border-white/5 p-6">
            <div className="flex items-start gap-3">
              <MapPin className="h-5 w-5 text-primary shrink-0 mt-0.5" />
              <div>
                <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Mailing address</p>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {business.legalName}<br />
                  {business.address.line1}<br />
                  {business.address.city}, {business.address.state} {business.address.zip}<br />
                  {business.address.country}
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-2xl bg-primary/10 border border-primary/30 p-6">
            <p className="text-xs uppercase tracking-widest text-primary font-bold mb-2">Order support tip</p>
            <p className="text-sm text-muted-foreground">
              Faster reply if you include your <span className="text-white">order ID</span> (looks like <code className="text-primary">NX8F2A…</code>) in the subject line.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
